import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, formatCalendarDate, Icon, Note, Popover } from '@repo/design-system';
import type { AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { space } from '@repo/design-tokens';

import { visuallyHidden } from '@repo/ui';

import {
  newStandardDateLimits,
  newStandardSentence,
  STANDARD_DEFAULT_NAME,
  STANDARD_MAX_DATE,
  draftFields,
  standardNameProblem,
  type StandardDraft,
  type StandardFields,
} from './hoursModel';
import StandardHoursFields from './StandardHoursFields';
import { actionProblem, HOURS_STILL_SAVING, type HoursActionResult } from './useOpeningHoursEditor';
import { usePopoverProblem } from './usePopoverProblem';

/**
 * *New Standard Hours From a Date* (spec twin-details-page-hours-week, R7.7): the trigger and its
 * popover. The new week starts as a copy of the hours in force the day before and ends the standard
 * covering the date the day before. It is named — *Standard hours* until the person types another —
 * and ends on the date chosen, or has no end, the default (spec opening-hours-edit-standard-details,
 * R4; decision 3 on the review page). Its fields are *Edit details*' (`StandardHoursFields`), so
 * adding and editing stay alike. The start panel mounts it too, as *Start on a later date instead*.
 */

export interface NewStandardHoursProps {
  versions: readonly AuthoredOpeningHoursVersion[];
  /** The twin's date (`launchHours.today`). */
  today: string;
  /** A save is waiting or running: the action waits. */
  busy: boolean;
  onAdd: (fields: StandardFields) => Promise<HoursActionResult>;
  onAdded: (fields: StandardFields) => void;
  /** An outcome that arrived after the popover was closed, for the step to show and say. */
  onLateProblem: (sentence: string) => void;
  /** The trigger's words and look: the list's foot (frame 06) or the start panel's later date. */
  label: string;
  variant: 'ghost' | 'primary';
  className?: string;
}

export const NEW_HOURS_CHANGED =
  'The hours changed while you were adding these. Check the dates and add them again.';
export const NEW_HOURS_UNKNOWN = 'Couldn’t tell whether the new hours were added.';

const EMPTY: StandardDraft = { name: STANDARD_DEFAULT_NAME, from: null, to: null, noEnd: true };

const NewStandardHours: React.FC<NewStandardHoursProps> = ({
  versions,
  today,
  busy,
  onAdd,
  onAdded,
  onLateProblem,
  label,
  variant,
  className,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  const reasonId = useId();
  const holdId = useId();
  const idPrefix = `new-standard-${useId().replace(/:/g, '')}`;
  // The read after a refused action can take this popover away with its owner: someone else removed
  // the week, or added the first one. What it shows goes to the step (R9), and so does an answer
  // still to come.
  const popover = usePopoverProblem(onLateProblem);
  const { open, problem: error, report, show } = popover;
  const [draft, setDraft] = useState<StandardDraft>(EMPTY);
  const [sending, setSending] = useState(false);

  const limits = newStandardDateLimits(versions, today);
  const unavailable = !limits.available;

  const close = () => {
    popover.close();
    setDraft(EMPTY);
  };

  const fields = draftFields(draft);
  const sentence = fields ? newStandardSentence(versions, fields) : null;
  const nameProblem = standardNameProblem(draft.name);
  const hold =
    nameProblem ??
    (!draft.from
      ? 'Choose when the new hours start.'
      : !fields
        ? 'Choose when the new hours end, or turn on No end date.'
        : draft.from > limits.max
          ? `Choose a start by ${formatCalendarDate(limits.max)}.`
          : null);

  const add = async () => {
    if (!fields || hold !== null || sending) return;
    setSending(true);
    report(null);
    const result = await onAdd(fields);
    setSending(false);
    const problem = actionProblem(result, NEW_HOURS_CHANGED, NEW_HOURS_UNKNOWN);
    if (problem === null) {
      close();
      onAdded(fields);
    } else {
      report(problem);
    }
  };

  const waitReason = unavailable
    ? 'These hours already run as far ahead as a new week can start.'
    : busy
      ? HOURS_STILL_SAVING
      : null;

  return (
    <span className={className}>
      <Button
        ref={triggerRef}
        variant={variant}
        size="sm"
        iconLeft={<Icon name="arrow-right-arrow-left" />}
        aria-disabled={waitReason !== null || undefined}
        aria-describedby={waitReason ? reasonId : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        title={waitReason ?? undefined}
        onClick={() => {
          if (waitReason === null) show();
        }}
      >
        {label}
      </Button>
      {waitReason && (
        <span id={reasonId} style={visuallyHidden}>
          {waitReason}
        </span>
      )}
      <Popover
        id={popoverId}
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        title="New standard hours"
        // Focus lands on the first field, *Name* (Popover's default).
        placement="top"
        width={360}
        actions={
          <>
            <Button variant="frosted" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              aria-label="Add new standard hours"
              aria-disabled={hold !== null || sending || undefined}
              aria-describedby={hold ? holdId : undefined}
              loading={sending}
              onClick={() => void add()}
            >
              Add
            </Button>
          </>
        }
      >
        <div className="body">
          <StandardHoursFields
            idPrefix={idPrefix}
            name={draft.name}
            onName={name => {
              setDraft(d => ({ ...d, name }));
              report(null);
            }}
            nameProblem={nameProblem}
            fixedStart={null}
            from={draft.from}
            to={draft.to}
            noEnd={draft.noEnd}
            onDates={dates => {
              setDraft(d => ({ ...d, ...dates }));
              report(null);
            }}
            start={{ min: limits.min, max: draft.noEnd ? limits.max : STANDARD_MAX_DATE }}
            end={{ min: limits.min, max: STANDARD_MAX_DATE }}
            noEndReason={null}
            today={today}
            disabled={sending}
            note={sentence ? [sentence] : []}
          />
          {hold && (
            <span id={holdId} style={visuallyHidden}>
              {hold}
            </span>
          )}
          {/* Polite, so the outcome is heard while focus stays on Add (R9). */}
          <div role="status">
            {error && (
              <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                {error}
              </Note>
            )}
          </div>
        </div>
      </Popover>
    </span>
  );
};

export default styled(NewStandardHours)`
  display: inline-flex;

  .body {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
  }
`;
